import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { Section } from "@/admin/_primitives";

const FIELDS = [
  { key: "disclaimer_text", label: "Disclaimer text", multiline: true, rows: 4 },
  { key: "upload_consent_text", label: "Upload consent text", multiline: true, rows: 3 },
  { key: "credit_hero_referral_url", label: "Credit Hero referral URL", testLink: true },
  { key: "business_formation_message", label: "Business formation message", multiline: true, rows: 2 },
  { key: "business_formation_referral_url", label: "Business formation referral URL", testLink: true },
  { key: "business_formation_partner_name", label: "Business formation partner name" },
];

const BUREAUS = ["experian", "equifax", "transunion"];

export default function AdminSiteSettings() {
  const { has } = useAdminPermissions();
  const canEdit = has("edit_site_settings");
  const canEditBureaus = has("edit_dispute_letters");
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [bureauAddresses, setBureauAddresses] = useState({});
  const [bureauInitial, setBureauInitial] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const settingsRes = await api.get("/site-settings");
      setInitial(settingsRes.data);
      setForm({ ...settingsRes.data, default_attachment_checklist: settingsRes.data.default_attachment_checklist || [] });
      if (canEditBureaus) {
        try {
          const bRes = await api.get("/admin/bureau-addresses");
          const map = {};
          (bRes.data || []).forEach((b) => { map[b.id] = b; });
          setBureauAddresses(JSON.parse(JSON.stringify(map)));
          setBureauInitial(JSON.parse(JSON.stringify(map)));
        } catch { /* tolerate */ }
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [canEditBureaus]);

  useEffect(() => { reload(); }, [reload]);

  const dirty = useMemo(() => {
    if (!initial || !form) return false;
    return JSON.stringify(initial) !== JSON.stringify({ ...initial, ...form }) ||
      FIELDS.some((f) => (form[f.key] ?? "") !== (initial[f.key] ?? "")) ||
      JSON.stringify(form.default_attachment_checklist || []) !== JSON.stringify(initial.default_attachment_checklist || []) ||
      JSON.stringify(bureauAddresses) !== JSON.stringify(bureauInitial);
  }, [initial, form, bureauAddresses, bureauInitial]);

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function updateChecklist(idx, v) {
    setForm((f) => {
      const list = [...(f.default_attachment_checklist || [])];
      list[idx] = v;
      return { ...f, default_attachment_checklist: list };
    });
  }
  function removeChecklist(idx) {
    setForm((f) => {
      const list = [...(f.default_attachment_checklist || [])];
      list.splice(idx, 1);
      return { ...f, default_attachment_checklist: list };
    });
  }
  function addChecklist() {
    setForm((f) => ({ ...f, default_attachment_checklist: [...(f.default_attachment_checklist || []), ""] }));
  }
  function moveChecklist(idx, delta) {
    setForm((f) => {
      const list = [...(f.default_attachment_checklist || [])];
      const target = idx + delta;
      if (target < 0 || target >= list.length) return f;
      [list[idx], list[target]] = [list[target], list[idx]];
      return { ...f, default_attachment_checklist: list };
    });
  }

  function updateBureau(bid, field, v) {
    setBureauAddresses((m) => ({ ...m, [bid]: { ...m[bid], [field]: v } }));
  }
  function updateBureauAddressLine(bid, idx, v) {
    setBureauAddresses((m) => {
      const lines = [...((m[bid]?.address_lines) || [])];
      lines[idx] = v;
      return { ...m, [bid]: { ...m[bid], address_lines: lines } };
    });
  }
  function addBureauAddressLine(bid) {
    setBureauAddresses((m) => ({ ...m, [bid]: { ...m[bid], address_lines: [...((m[bid]?.address_lines) || []), ""] } }));
  }
  function removeBureauAddressLine(bid, idx) {
    setBureauAddresses((m) => {
      const lines = [...((m[bid]?.address_lines) || [])];
      lines.splice(idx, 1);
      return { ...m, [bid]: { ...m[bid], address_lines: lines } };
    });
  }

  async function save() {
    if (!form || !initial) return;
    setSaving(true);
    try {
      // Settings diff payload.
      const payload = {};
      for (const f of FIELDS) {
        if ((form[f.key] ?? "") !== (initial[f.key] ?? "")) payload[f.key] = form[f.key] ?? "";
      }
      const newChecklist = (form.default_attachment_checklist || []).map((s) => s.trim()).filter(Boolean);
      const oldChecklist = (initial.default_attachment_checklist || []).filter(Boolean);
      if (JSON.stringify(newChecklist) !== JSON.stringify(oldChecklist)) {
        payload.default_attachment_checklist = newChecklist;
      }
      if (Object.keys(payload).length > 0) {
        await api.patch("/admin/site-settings", payload);
      }
      // Bureau-address diffs.
      const updates = [];
      for (const [bid, current] of Object.entries(bureauAddresses)) {
        const before = bureauInitial[bid] || {};
        const diff = {};
        if ((current.recipient_name || "") !== (before.recipient_name || "")) diff.recipient_name = current.recipient_name || "";
        if (JSON.stringify(current.address_lines || []) !== JSON.stringify(before.address_lines || [])) diff.address_lines = current.address_lines || [];
        if (Object.keys(diff).length > 0) updates.push(api.patch(`/admin/bureau-addresses/${bid}`, diff));
      }
      if (updates.length) await Promise.all(updates);
      toast.success("Site settings saved");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  function revert() {
    setForm({ ...initial, default_attachment_checklist: [...(initial.default_attachment_checklist || [])] });
    setBureauAddresses(JSON.parse(JSON.stringify(bureauInitial)));
  }

  if (loading || !form) {
    return <AdminLayout title="Site settings"><div className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div></AdminLayout>;
  }

  return (
    <AdminLayout
      title="Site settings"
      subtitle="Compliance copy, referral URLs, and dispute bureau addresses"
      actions={
        canEdit && (
          <div className="flex items-center gap-2">
            <button type="button" data-testid="site-settings-revert" onClick={revert} disabled={!dirty || saving} className="rounded-[var(--ds-radius-md)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-[12px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] disabled:opacity-40">
              Revert
            </button>
            <button type="button" data-testid="site-settings-save" onClick={save} disabled={!dirty || saving} className="rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold disabled:opacity-50 transition-colors" style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)", transitionDuration: "var(--ds-duration-fast)" }} onMouseEnter={(e) => !(saving || !dirty) && (e.currentTarget.style.background = "var(--ds-color-accent-strong)")} onMouseLeave={(e) => (e.currentTarget.style.background = "var(--ds-color-accent)")}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )
      }
    >
      <AdminPageHelp
        title="Site Settings"
        purpose="Central control for the platform's compliance copy, referral URLs, dispute bureau addresses, and default attachment checklist."
        configures="Disclaimer + upload-consent copy; Credit Hero and business-formation referral partner URLs and names; dispute-letter attachment checklist; per-bureau (Experian/Equifax/TransUnion) mailing addresses."
        impact="Consumer-facing surfaces (landing page, upload consent modal, dispute letters) pick up the new copy immediately on next page load. Existing draft letters are NOT rewritten — only newly generated letters use the updated addresses/checklist."
        safety="Yes. All changes are diffable — the audit log records the before/after value of every field, and you can paste the previous value back and re-save to revert. Bureau addresses ship with sensible platform defaults."
      />
      <div className="space-y-6">
        {!canEdit && (
          <div className="rounded-md border border-[var(--ds-color-warning-soft)] bg-[var(--ds-color-warning-soft)] p-3 text-xs text-[var(--ds-color-warning)]">
            Your role can view these settings but cannot save changes.
          </div>
        )}
        <Section title="Compliance copy">
          {FIELDS.slice(0, 2).map((field) => (
            <Field key={field.key} field={field} value={form[field.key]} onChange={(v) => update(field.key, v)} disabled={!canEdit} />
          ))}
        </Section>
        <Section title="Referral partners">
          {FIELDS.slice(2, 6).map((field) => (
            <Field key={field.key} field={field} value={form[field.key]} onChange={(v) => update(field.key, v)} disabled={!canEdit} />
          ))}
        </Section>
        <Section title="Default attachment checklist (dispute letters)" actions={canEdit && (
          <button type="button" data-testid="site-settings-checklist-add" onClick={addChecklist} className="text-xs text-[var(--ds-color-accent)] hover:underline inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add item
          </button>
        )}>
          <ul className="space-y-2">
            {(form.default_attachment_checklist || []).map((item, idx) => (
              <li key={idx} className="flex gap-2 items-center" data-testid={`site-settings-checklist-row-${idx}`}>
                <input
                  type="text"
                  value={item}
                  disabled={!canEdit}
                  onChange={(e) => updateChecklist(idx, e.target.value)}
                  data-testid={`site-settings-checklist-input-${idx}`}
                  aria-label={`Checklist item ${idx + 1}`}
                  className="flex-1 rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-1.5 text-sm text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
                />
                {canEdit && (
                  <>
                    <button type="button" aria-label={`Move item ${idx + 1} up`} onClick={() => moveChecklist(idx, -1)} className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"><ArrowUp className="w-3.5 h-3.5" aria-hidden="true" /></button>
                    <button type="button" aria-label={`Move item ${idx + 1} down`} onClick={() => moveChecklist(idx, 1)} className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"><ArrowDown className="w-3.5 h-3.5" aria-hidden="true" /></button>
                    <button type="button" data-testid={`site-settings-checklist-remove-${idx}`} aria-label={`Remove item ${idx + 1}`} onClick={() => removeChecklist(idx)} className="text-[var(--ds-color-danger)] hover:text-[var(--ds-color-danger)]"><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></button>
                  </>
                )}
              </li>
            ))}
            {(form.default_attachment_checklist || []).length === 0 && (
              <li className="text-xs text-[var(--ds-color-ink-muted)]">No items — add the first one above.</li>
            )}
          </ul>
        </Section>
        {canEditBureaus && (
          <Section title="Bureau addresses">
            <div className="grid md:grid-cols-3 gap-4">
              {BUREAUS.map((bid) => {
                const ba = bureauAddresses[bid] || { recipient_name: "", address_lines: [] };
                return (
                  <div key={bid} data-testid={`site-settings-bureau-${bid}`} className="rounded-md border border-[var(--ds-color-admin-border)] p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-accent)] mb-2">{bid}</div>
                    <label className="text-xs text-[var(--ds-color-ink-muted)]">
                      <span className="block mb-1 uppercase tracking-[0.12em] text-[10px]">Recipient name</span>
                      <input
                        type="text"
                        value={ba.recipient_name || ""}
                        onChange={(e) => updateBureau(bid, "recipient_name", e.target.value)}
                        data-testid={`site-settings-bureau-${bid}-recipient`}
                        className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-1.5 text-sm text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
                      />
                    </label>
                    <div className="text-xs text-[var(--ds-color-ink-muted)] mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="uppercase tracking-[0.12em] text-[10px]">Address lines</span>
                        <button type="button" onClick={() => addBureauAddressLine(bid)} className="text-[var(--ds-color-accent)] text-[10px] hover:underline inline-flex items-center gap-0.5">
                          <Plus className="w-2.5 h-2.5" /> Line
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {(ba.address_lines || []).map((line, idx) => (
                          <div key={idx} className="flex items-center gap-1">
                            <input
                              type="text"
                              value={line}
                              onChange={(e) => updateBureauAddressLine(bid, idx, e.target.value)}
                              data-testid={`site-settings-bureau-${bid}-line-${idx}`}
                              aria-label={`${bid} address line ${idx + 1}`}
                              className="flex-1 rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2 py-1 text-xs text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
                            />
                            <button
                              type="button"
                              onClick={() => removeBureauAddressLine(bid, idx)}
                              aria-label={`Remove ${bid} address line ${idx + 1}`}
                              className="text-[var(--ds-color-danger)]"
                            >
                              <Trash2 className="w-3 h-3" aria-hidden="true" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}
        <div className="text-xs text-[var(--ds-color-ink-muted)] pt-4 border-t border-[var(--ds-color-admin-border)]">
          Last updated: {initial?.updated_at ? new Date(initial.updated_at).toLocaleString() : "—"}
        </div>
      </div>
    </AdminLayout>
  );
}

function Field({ field, value, onChange, disabled }) {
  return (
    <label className="block mb-3">
      <span
        className="block mb-1 text-[10.5px] uppercase tracking-[0.16em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {field.label}
      </span>
      {field.multiline ? (
        <textarea
          value={value ?? ""}
          disabled={disabled}
          rows={field.rows || 3}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`site-settings-field-${field.key}`}
          className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
          style={{ color: "var(--ds-color-ink)" }}
        />
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={value ?? ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`site-settings-field-${field.key}`}
            className="flex-1 rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
            style={{ color: "var(--ds-color-ink)" }}
          />
          {field.testLink && value && (
            <button
              type="button"
              onClick={() => window.open(value, "_blank")}
              className="text-[11px] inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--ds-color-accent)" }}
            >
              <ExternalLink className="w-3 h-3" /> Test
            </button>
          )}
        </div>
      )}
    </label>
  );
}

